import { registerRemotes } from "@module-federation/enhanced/runtime";
import type { AppModule } from "@tc-superpods/app-contract";
import type { AppStatus, IsolatedMountPoint, RegistryEntry } from "./types";

const registeredRemotes = new Set<string>();

export function renderAppHost(
  outlet: HTMLElement,
  entry: RegistryEntry,
  status: AppStatus
): HTMLElement {
  // Approved exception: the shell's remote mount container is intentionally created with
  // low-level DOM APIs because federated apps mount against a concrete host element.
  const hostElement = document.createElement("section");
  hostElement.dataset.platformAppHost = "";
  hostElement.dataset.registryAppId = entry.id;
  hostElement.dataset.appRoute = entry.route;
  hostElement.dataset.remoteEntry = entry.entry;
  hostElement.dataset.appStatus = status;

  outlet.replaceChildren(hostElement);

  return hostElement;
}

export function createIsolatedMountPoint(hostElement: HTMLElement): IsolatedMountPoint {
  const shadowRoot = hostElement.attachShadow({ mode: "open" });
  const container = document.createElement("div");
  container.style.cssText = "height:100%;width:100%;";
  shadowRoot.appendChild(container);

  return {
    container,
    shadowRoot
  };
}

export function renderShellError(outlet: HTMLElement, message: string, status: AppStatus): void {
  // Approved exception: this shell-owned fallback is rendered directly into the mount
  // container so route-level load and mount failures can be reported without depending on a
  // remote or additional React reconciliation at this boundary.
  const errorElement = document.createElement("section");
  errorElement.dataset.platformAppHost = "";
  errorElement.dataset.appStatus = status;
  errorElement.setAttribute("role", "alert");
  errorElement.textContent = message;

  outlet.replaceChildren(errorElement);
}

export function registerRemote(entry: RegistryEntry): void {
  if (registeredRemotes.has(entry.id)) {
    return;
  }

  // Angular remotes emit an ES-module container (loaded via dynamic import);
  // Vite/rspack remotes emit a classic-script container (the runtime default).
  // `entryType` in the registry entry selects which — default "global".
  registerRemotes([{ name: entry.id, entry: entry.entry, type: entry.entryType ?? "global" }]);
  registeredRemotes.add(entry.id);
}

export function isAppModule(value: unknown): value is AppModule {
  if (!isRecord(value)) {
    return false;
  }

  return typeof value.mount === "function" && typeof value.unmount === "function";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
